/**
 * Custom hook for filtering logos based on language and university context
 */
import { useMemo } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { FooterLogoProps } from '@designsystem/stories/04-organisms/Footer';
import { normalizeContent } from '@fhnw/helpers/DesignSystem/normalizeContent';
import { logos } from './Footer.data';
import { DEFAULT_LANGUAGE } from './Footer.constants';
import { filterAndMapLogos, getUniversityId, isString } from './Footer.utils';

interface NavContent extends Omit<Content, '@components'> {
  '@components'?: {
    university?: Content;
  };
}

/**
 * Hook to filter and map logos based on current language and university
 *
 * @param content - Navigation content from Plone
 * @returns Filtered array of footer logo props
 */
export const useFilteredLogos = (content?: NavContent): FooterLogoProps[] => {
  const language = content?.language;
  const university = content?.['@components']?.university;

  // derive stable primitive dependencies
  const currentLang = normalizeContent(language, false);
  const lang = isString(currentLang) ? currentLang : DEFAULT_LANGUAGE;
  const universityId = getUniversityId(university);

  return useMemo(() => {
    return filterAndMapLogos(logos, lang, universityId);
  }, [lang, universityId]);
};
